import React, { useState, useEffect, useRef } from 'react';

// Extract TikTok video ID from URL
const extractTikTokVideoId = (url) => {
  if (!url) return null;

  try {
    // Format: https://www.tiktok.com/@username/video/1234567890
    const match = url.match(/tiktok\.com\/@[\w.-]+\/video\/(\d+)/);
    if (match && match[1]) {
      return match[1];
    }

    // Format: https://vm.tiktok.com/xxxxx or https://vt.tiktok.com/xxxxx
    // These should already be resolved by backend, but if not, we'll show error
    const shortMatch = url.match(/(?:vm|vt)\.tiktok\.com\/([\w]+)/);
    if (shortMatch && shortMatch[1]) {
      // Short links should be resolved by backend before saving
      // If we still see short link here, it means resolution failed
      return null;
    }

    return null;
  } catch (error) {
    return null;
  }
};

const TikTokEmbed = ({ url, className = '' }) => {
  const [videoId, setVideoId] = useState(null);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);
  const processedRef = useRef(false);

  useEffect(() => {
    const id = extractTikTokVideoId(url);
    if (id) {
      setVideoId(id);
      setError(null);
      
      // Load TikTok embed script if not already loaded
      if (!window.tiktokEmbedLoaded) {
        const script = document.createElement('script');
        script.src = 'https://www.tiktok.com/embed.js';
        script.async = true;
        script.onload = () => {
          window.tiktokEmbedLoaded = true;
          // Process all embeds after script loads
          setTimeout(() => {
            if (window.tiktokEmbed && typeof window.tiktokEmbed.process === 'function') {
              window.tiktokEmbed.process();
            }
          }, 100);
        };
        document.body.appendChild(script);
        window.tiktokEmbedLoaded = true; // Set immediately to prevent multiple loads
      }
      
      // Process embed after a delay to ensure blockquote is rendered
      // This will also process all embeds on the page
      const processTimer = setTimeout(() => {
        // TikTok embed script automatically processes blockquote elements
        // But we can trigger it manually if needed
        if (window.tiktokEmbed && typeof window.tiktokEmbed.process === 'function') {
          window.tiktokEmbed.process();
          processedRef.current = true;
        }
      }, 600);

      // Also process when container is mounted/updated
      const processTimer2 = setTimeout(() => {
        if (window.tiktokEmbed && typeof window.tiktokEmbed.process === 'function') {
          window.tiktokEmbed.process();
        }
      }, 1200);

      // Reset processed flag when URL changes
      processedRef.current = false;

      return () => {
        clearTimeout(processTimer);
        clearTimeout(processTimer2);
      };
    } else {
      setVideoId(null);
      setError('Tidak dapat mengekstrak video ID dari URL TikTok. Pastikan URL dalam format: https://www.tiktok.com/@username/video/1234567890');
    }
  }, [url]);

  // Process embed when container is ready
  useEffect(() => {
    if (!videoId) return;
    
    // Process embed if script is loaded, wait for container to be ready
    // Use multiple timeouts to ensure embed is processed even after re-render
    const processTimer1 = setTimeout(() => {
      if (containerRef.current && window.tiktokEmbed && typeof window.tiktokEmbed.process === 'function') {
        window.tiktokEmbed.process();
      }
    }, 300);

    const processTimer2 = setTimeout(() => {
      if (containerRef.current && window.tiktokEmbed && typeof window.tiktokEmbed.process === 'function') {
        window.tiktokEmbed.process();
      }
    }, 800);

    const processTimer3 = setTimeout(() => {
      if (containerRef.current && window.tiktokEmbed && typeof window.tiktokEmbed.process === 'function') {
        window.tiktokEmbed.process();
      }
    }, 1500);

    return () => {
      clearTimeout(processTimer1);
      clearTimeout(processTimer2);
      clearTimeout(processTimer3);
    };
  }, [videoId]);

  // Monitor iframe after it's created to disable autoplay
  useEffect(() => {
    if (!videoId || !containerRef.current) return;

    const checkAndDisableAutoplay = () => {
      const iframes = containerRef.current.querySelectorAll('iframe');
      iframes.forEach(iframe => {
        // Remove autoplay parameter from iframe src
        if (iframe.src && (iframe.src.includes('autoplay=1') || iframe.src.includes('autoplay=true'))) {
          const newSrc = iframe.src
            .replace(/[?&]autoplay=1/g, '')
            .replace(/[?&]autoplay=true/g, '')
            .replace(/[?&]autoplay=[^&]*/g, '');
          if (newSrc !== iframe.src) {
            iframe.src = newSrc;
          }
        }
      });
    };

    // Use MutationObserver to watch for iframe changes
    const observer = new MutationObserver(() => {
      checkAndDisableAutoplay();
    });

    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src']
    });

    // Check immediately and periodically
    checkAndDisableAutoplay();
    const interval = setInterval(checkAndDisableAutoplay, 500);
    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 5000); // Stop checking after 5 seconds

    return () => {
      observer.disconnect();
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [videoId]);

  if (error) {
    return (
      <div className={`bg-gray-100 rounded-lg p-4 ${className}`}>
        <p className="text-sm text-gray-600">{error}</p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-indigo-600 hover:text-indigo-700 text-sm mt-2 inline-block"
        >
          Buka link di tab baru →
        </a>
      </div>
    );
  }

  if (!videoId) {
    return null;
  }

  return (
    <div 
      ref={containerRef}
      className={`tiktok-embed-container my-4 ${className}`}
    >
      <blockquote
        className="tiktok-embed"
        cite={url}
        data-video-id={videoId}
        style={{ maxWidth: '100%', minWidth: '325px' }}
      >
        <section>
          <a
            target="_blank"
            rel="noopener noreferrer"
            title={`TikTok Video ${videoId}`}
            href={url}
          >
            {url}
          </a>
        </section>
      </blockquote>
    </div>
  );
};

// Remove memo to ensure re-render when component is mounted
export default TikTokEmbed;

